// Click-to-load facade for the core video widget, forked from
// apostrophe/modules/@apostrophecms/video-widget/ui/src/index.js — a project-level
// ui/src/index.js replaces the core file wholesale, so re-check this on upgrades.
//
// The oembed lookup is already private: it goes to our own server, which queries the
// provider server-side, so the visitor's IP never reaches YouTube. What leaks is the
// iframe that lookup returns — so we render a poster from the metadata and hold the
// embed back until the visitor asks for it. The provider's own thumbnail is
// deliberately unused; i.ytimg.com is Google too.
export default () => {
  apos.oembedCache ||= {};
  apos.util.widgetPlayers['@apostrophecms/video'] = {
    selector: '[data-apos-video-widget]',
    player: function (el) {
      const videoUrl = el.getAttribute('data-apos-video-url');
      if (!videoUrl) {
        return;
      }

      query(videoUrl).then((result) => {
        if (result.type !== 'video') {
          return fail('inappropriate');
        }
        renderFacade(result);
      }).catch(fail);

      async function query(url) {
        if (Object.hasOwn(apos.oembedCache, url)) {
          return apos.oembedCache[url];
        }
        apos.util.addClass(el, 'apos-oembed-busy');
        const result = await apos.http.get('/api/v1/@apostrophecms/oembed/query', { qs: { url } });
        apos.oembedCache[url] = result;
        return result;
      }

      function renderFacade(result) {
        apos.util.removeClass(el, 'apos-oembed-busy');
        const label = el.getAttribute('data-apos-video-label') || '';

        const facade = document.createElement('div');
        facade.className = 'placeholder-img ratio video-facade';
        facade.setAttribute('role', 'button');
        facade.setAttribute('tabindex', '0');
        facade.setAttribute('aria-label', result.title ? `${label}: ${result.title}` : label);
        // Match the real player's shape up front, so swapping it in shifts nothing.
        if (result.width && result.height) {
          facade.style.setProperty('--bs-aspect-ratio', `${(100 * result.height) / result.width}%`);
        } else {
          facade.classList.add('ratio-16x9');
        }

        const play = document.createElement('span');
        play.className = 'video-facade-play';
        play.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          '<circle cx="12" cy="12" r="11"/><path d="M9.5 7.5 17 12l-7.5 4.5z"/></svg>';
        facade.appendChild(play);

        if (result.title) {
          const title = document.createElement('span');
          title.className = 'video-facade-title';
          // textContent, not innerHTML — the title is provider-supplied.
          title.textContent = result.title;
          facade.appendChild(title);
        }

        function activate(event) {
          if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          event.preventDefault();
          injectEmbed(result);
        }
        // Bound directly rather than delegated: this element is ours, so it can never
        // collide with the blog card facade's document-level handler.
        facade.addEventListener('click', activate);
        facade.addEventListener('keydown', activate);

        el.replaceChildren(facade);
      }

      function injectEmbed(result) {
        const shaker = document.createElement('div');
        shaker.innerHTML = result.html;
        const inner = shaker.firstChild;
        if (!inner) {
          return fail('empty embed');
        }
        inner.setAttribute('data-apos-video-canvas', '');
        inner.removeAttribute('width');
        inner.removeAttribute('height');
        if (result.width && result.height) {
          inner.style.width = '100%';
          inner.style.aspectRatio = `${result.width} / ${result.height}`;
        }
        // The click that loaded the player should also start it.
        if (inner.tagName === 'IFRAME' && inner.src) {
          try {
            const url = new URL(inner.src, window.location.href);
            url.searchParams.set('autoplay', '1');
            inner.src = url.toString();
          } catch (error) {
            // Leave the provider's src alone if it will not parse.
          }
        }
        el.replaceChildren(inner);
        if (inner.tagName === 'IFRAME') {
          inner.focus();
        }
      }

      function fail(err) {
        apos.util.removeClass(el, 'apos-oembed-busy');
        apos.util.addClass(el, 'apos-oembed-invalid');
        console.error(err);
        el.replaceChildren();
      }
    }
  };
};
